// ============================================================
// CONEXIÓN CON ZENTRIA (el inventario vive en otro proyecto)
// ============================================================
//
// La web de Aceros Lima es un proyecto Firebase propio (`aceros-lima`), pero su
// catálogo ya no se administra aquí: sale del inventario que la empresa lleva
// en CGO, que vive en el proyecto del ecosistema (`zenlogic-8a575`). Son dos
// proyectos distintos, así que hace falta una SEGUNDA app de Firebase.
//
// Solo se usa para leer `tenants/{EMPRESA}/cgo_catalogo_publico`, que es una
// proyección recortada del inventario: nombre, precio de venta, medidas y si
// hay existencias. El inventario de verdad —con el costo de compra y el stock
// exacto— sigue cerrado y nunca llega al navegador.
//
// Estos valores son públicos por diseño (viajan en cualquier bundle que hable
// con ese proyecto); lo que protege los datos son las reglas de Firestore.

import { getApp, getApps, initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { slug } from "./format";

const NOMBRE_APP = "zentria";

const configZentria = {
  apiKey: "AIzaSyBo3gGsIr5f5KDttAr25hC9g_YgInGpLi8",
  authDomain: "zenlogic-8a575.firebaseapp.com",
  projectId: "zenlogic-8a575",
  storageBucket: "zenlogic-8a575.firebasestorage.app",
};

/** Identificador de Grupo Aceros Lima dentro de Zentria. */
export const EMPRESA_ID = "aceroslima";

/** Colección pública que alimenta la tienda. */
export const COLECCION_CATALOGO = "cgo_catalogo_publico";

/**
 * Enlace al inventario del cliente en CGO.
 *
 * CGO se sirve en el subdominio del propio cliente (cgo.aceroslima.com), asi
 * que se deduce del dominio donde este corriendo la tienda en vez de dejarlo
 * fijo: si manana el sitio cambia de dominio, el enlace lo sigue.
 *
 * CGO resuelve la empresa por la sesion de quien entra, no por el host, de modo
 * que el subdominio es la puerta del cliente y no hace falta pasarle nada mas.
 */
const CGO_COMPARTIDO = "https://cgo.ayatech.com.pe";
const SAC_COMPARTIDO = "https://perimetroapp-699e5.web.app";

/** Dominio propio del cliente, o null si esto no corre en uno. */
function dominioPropio() {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase().replace(/^www\./, "");
  const valido =
    host.split(".").length >= 2 &&
    !host.endsWith(".web.app") &&
    !host.endsWith(".firebaseapp.com") &&
    host !== "localhost" &&
    host !== "127.0.0.1";
  return valido ? host : null;
}

// En localhost o en el dominio de despliegue de Firebase no existen los
// subdominios del cliente: se cae al de la plataforma.
export function urlCGO(ruta = "/inventory") {
  const host = dominioPropio();
  return host ? `https://cgo.${host}${ruta}` : CGO_COMPARTIDO + ruta;
}

/** Portal de cotizaciones SAC del cliente. */
export function urlSAC(ruta = "") {
  const host = dominioPropio();
  return host ? `https://sac.${host}${ruta}` : SAC_COMPARTIDO + ruta;
}

function appZentria() {
  // `initializeApp` con un nombre ya usado lanza; en desarrollo, con recarga en
  // caliente, este módulo se evalúa más de una vez.
  return getApps().some((a) => a.name === NOMBRE_APP)
    ? getApp(NOMBRE_APP)
    : initializeApp(configZentria, NOMBRE_APP);
}

export const dbZentria = getFirestore(appZentria());

/**
 * Traduce un artículo del catálogo público a la forma que ya usa la tienda.
 *
 * El diccionario de atributos viene tal cual del inventario, que es genérico
 * (CGO sirve a talleres, tiendas y almacenes). La presentación —qué etiqueta
 * lleva cada dato y cómo se escribe una medida— es cosa de esta web, no de CGO.
 */
const ETIQUETAS = {
  calidad: "norma",
  espesor: "espesor",
  pesoUnit: "pesoUnitario",
  acabado: "acabado",
  diametro: "diametro",
  longitud: "longitud",
};

const mm = (v) => (Number.isFinite(Number(v)) ? `${Number(v)} mm` : null);

export function aProductoDeTienda(id, item) {
  const attrs = item?.atributos || {};
  const especificaciones = {};

  for (const [clave, valor] of Object.entries(attrs)) {
    if (valor === null || valor === undefined || valor === "") continue;
    const destino = ETIQUETAS[clave] || clave;
    especificaciones[destino] =
      clave === "espesor" ? mm(valor) || valor : valor;
  }

  // Ancho y largo se guardan sueltos en el inventario; en una tienda de acero
  // se leen juntos ("1.20 x 2.40 m") y así es como los espera la ficha.
  const ancho = Number(attrs.ancho);
  const largo = Number(attrs.largo);
  if (Number.isFinite(ancho) && Number.isFinite(largo) && ancho > 0 && largo > 0) {
    especificaciones.medida = `${(ancho / 1000).toFixed(2)} x ${(largo / 1000).toFixed(2)} m`;
    delete especificaciones.ancho;
    delete especificaciones.largo;
  }

  return {
    id,
    sku: item?.sku || "",
    nombre: item?.nombre || "",
    // En CGO la familia es una etiqueta legible ("Planchas metalicas"); aqui
    // hace falta ademas un identificador para filtrar y para la URL.
    categoria: slug(item?.categoria || ""),
    categoriaEtiqueta: item?.categoria || "",
    descripcion: item?.descripcion || "",
    especificaciones,
    unidad: item?.unidad || "unidad",
    precio: Number(item?.precio) || 0,
    // La tienda solo sabe si hay o no hay. La cantidad exacta se queda en el
    // inventario, que es de la empresa.
    disponible: item?.disponible !== false,
    imagen: item?.imagen || "",
    activo: true,
    orden: 0,
  };
}
